/**
 * 客户数据导入中心
 * 路由：/admin/data-import (admin/director) · /team/data-import (manager) · /import (sales)
 *
 * 8 步向导：
 *   1. 选导入类型（8 类）
 *   2. 选来源（钉钉 / 通用模板 / CSV）
 *   3. 上传文件（拖拽 / 粘贴 / .xlsx 或 .zip 包裹 xlsx）
 *   4. 字段映射预览（自动 + 手动调整 + 可保存为模板）
 *   5. dry-run 预检（成功/失败/跳过 报告）
 *   6. 确认导入（销售员→主管审批 / 主管+ 直接执行）
 *   7. 实时进度 + 结果（含失败行 Excel 下载）
 *   8. 截图（单独 /import/screenshots 入口）
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Select, Upload, Button, Tabs, Tag, Progress, Empty, Modal, Input, message } from 'antd';
import type { UploadProps } from 'antd';
import { $ } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { PageHeader, Pill, Btn, StatsRow, StatCard } from '../../components/ui';
import { toast } from '../../lib/toast';
import { fmtDateTimeSec, fmtRelative } from '../../lib/format';

const IMPORT_TYPES = [
  { key: 'customers',  label: '客户主表',   tag: '最常用', desc: '昵称 / 手机号 / 来源 / 阶段 / 跟进人' },
  { key: 'follow_ups', label: '跟进记录',   tag: '',     desc: '"第N天跟踪情况" 10 列自动拆为多条' },
  { key: 'contracts',  label: '合同',       tag: '',     desc: '签约人 / 服务期 / 金额' },
  { key: 'invoices',   label: '发票',       tag: '',     desc: '发票编号 / 抬头 / 金额' },
  { key: 'payments',   label: '收款',       tag: '',     desc: '定金 / 尾款 / 催收' },
  { key: 'shipments',  label: '邮寄',       tag: '',     desc: '快递单号 / 地址（AI 解析收件人）' },
  { key: 'materials',  label: '学员资料',   tag: '',     desc: '身份证 / 学历 / 二寸照' },
  { key: 'orders',     label: '订单',       tag: '',     desc: '班型 / 金额 / 提成 / 审批' },
] as const;
type ImportType = (typeof IMPORT_TYPES)[number]['key'];

const SOURCE_TYPES = [
  { key: 'dingtalk_xlsx', label: '钉钉 AI 表格导出', desc: '自动按 spec 10.4 字段映射' },
  { key: 'template_xlsx', label: '通用模板',         desc: '下载模板填好再传 — 字段完全对齐新系统' },
  { key: 'csv',           label: 'CSV 文件',         desc: '通用纯文本格式' },
] as const;
type SourceType = (typeof SOURCE_TYPES)[number]['key'];

const CONFLICT_STRATEGY = [
  { key: 'skip',      label: '跳过',  desc: '客户已存在 → 保留旧数据，不改' },
  { key: 'overwrite', label: '覆盖',  desc: '客户已存在 → 用新数据覆盖' },
  { key: 'transfer',  label: '转让',  desc: '客户已存在 → 把客户转给我导入指定的销售员' },
] as const;

interface DryRunRowMeta {
  row: number;
  action: 'import' | 'skip' | 'fail' | 'warn';
  reason?: string;
  severity?: 'info' | 'warning' | 'error';
  parsedData?: Record<string, any>;
}
interface DryRunResultMeta {
  total: number;
  willImport: number;
  willSkip: number;
  willFail: number;
  warnings: number;
  rows: DryRunRowMeta[];
  errorSummary: Array<{ reason: string; count: number }>;
}
interface JobState {
  id: number;
  status: string;
  importType: ImportType;
  totalRows: number;
  successCount: number;
  failCount: number;
  skipCount: number;
  fileName: string;
  createdAt?: string;
  fieldMapping: Record<string, string> | null;
  conflictStrategy: string;
  errorReportUrl?: string | null;
  insertedRowIds?: number[] | null;
  notes?: string | null;
  dryRunResult?: DryRunResultMeta | null;
  // C-4 进度推送
  progressPercentage?: number;
  currentPhase?: string | null;
  // C-4.5 沙盒标记
  isSandbox?: number | boolean;
}

interface SchemaField {
  target: string;
  type: string;
  required?: boolean;
  enumMap?: Record<string, string>;
  note?: string;
}
interface ImportSchema {
  label: string;
  sources: Record<string, SchemaField>;
  note?: string;
}

export function DataImportPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const isSales = role === 'sales';
  const breadcrumb = useMemo(() => {
    if (role === 'admin' || role === 'director') return ['系统', '数据导入'];
    if (role === 'manager') return ['团队', '数据导入'];
    return ['工具', '我的导入'];
  }, [role]);

  const [step, setStep] = useState<number>(1);
  const [importType, setImportType] = useState<ImportType | null>(null);
  const [sourceType, setSourceType] = useState<SourceType | null>(null);
  const [job, setJob] = useState<JobState | null>(null);
  const [parsedColumns, setParsedColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<'skip' | 'overwrite' | 'transfer'>('skip');
  // C-4.5: 沙盒模式 toggle — Step 6 决定后传给 execute() body
  const [useSandbox, setUseSandbox] = useState<boolean>(false);

  const { data: schemas } = useQuery({
    queryKey: ['imports-schemas'],
    queryFn: () => $.get<Record<string, ImportSchema>>('/imports/schemas'),
  });
  const currentSchema = importType ? schemas?.[importType] : null;

  const { data: history } = useQuery({
    queryKey: ['imports-list'],
    queryFn: () => $.get<JobState[]>('/imports'),
  });

  /** 上传文件 */
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('importType', importType!);
      fd.append('sourceType', sourceType!);
      // 直接用 axios 实例发 multipart
      const { api } = await import('../../lib/api');
      const r = await api.post('/imports/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data as { job: JobState; parsed: { columns: string[]; autoMapping: Record<string, string>; totalRows: number } };
    },
    onSuccess: (res) => {
      setJob(res.job);
      setParsedColumns(res.parsed.columns);
      setMapping(res.parsed.autoMapping ?? {});
      setStep(4);
      toast.success(`已上传：${res.parsed.totalRows} 行 · 自动识别 ${Object.keys(res.parsed.autoMapping ?? {}).length} 列字段`);
      qc.invalidateQueries({ queryKey: ['imports-list'] });
    },
  });

  /** 保存字段映射 */
  const saveMapping = useMutation({
    mutationFn: () => $.post(`/imports/${job!.id}/mapping`, { fieldMapping: mapping }),
    onSuccess: () => setStep(5),
  });

  /** dry-run */
  const dryRun = useMutation({
    mutationFn: () => $.post<JobState>(`/imports/${job!.id}/dry-run`),
    onSuccess: (res: any) => { setJob(res); },
  });

  /** 确认导入 */
  const execute = useMutation({
    mutationFn: async () => {
      await $.post(`/imports/${job!.id}/conflict-strategy`, { strategy: conflict });
      // C-4.5: 沙盒标记 — execute body 携带 isSandbox
      return $.post<JobState>(`/imports/${job!.id}/execute`, { isSandbox: useSandbox });
    },
    onSuccess: (res: any) => {
      setJob(res);
      setStep(7);
      toast.success(
        useSandbox ? '已写入沙盒，验证后再正式提交' :
        isSales ? '已提交主管审批' : '导入任务已启动'
      );
    },
  });

  const restart = () => {
    setStep(1);
    setImportType(null);
    setSourceType(null);
    setJob(null);
    setMapping({});
    setParsedColumns([]);
  };

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title="数据导入中心"
        subtitle={isSales ? '导入你的客户 · 需主管审批' : '把钉钉表格 / 模板 / CSV 数据搬进系统'}
        actions={
          <>
            <Btn kind="text" onClick={() => setStep(0)}>导入历史 / 撤销 →</Btn>
            <Btn kind="text" onClick={() => nav('/import/screenshots')}>批量上传截图 →</Btn>
          </>
        }
      />
      <div className="work-content">
        <StepIndicator step={step} />

        {step === 1 && (
          <Step1Type onSelect={(t) => { setImportType(t); setStep(2); }} />
        )}
        {step === 2 && (
          <Step2Source
            onSelect={(s) => { setSourceType(s); setStep(3); }}
            onBack={() => setStep(1)}
            importType={importType!}
          />
        )}
        {step === 3 && (
          <Step3Upload
            onUpload={(f) => upload.mutate(f)}
            uploading={upload.isPending}
            onBack={() => setStep(2)}
          />
        )}
        {step === 4 && job && currentSchema && (
          <Step4Mapping
            columns={parsedColumns}
            schema={currentSchema}
            mapping={mapping}
            setMapping={setMapping}
            onNext={() => saveMapping.mutate()}
            onBack={() => setStep(3)}
            saving={saveMapping.isPending}
            importType={importType!}
            sourceType={sourceType!}
          />
        )}
        {step === 5 && job && (
          <Step5DryRun
            job={job}
            running={dryRun.isPending}
            onRun={() => dryRun.mutate()}
            onNext={() => setStep(6)}
            onBack={() => setStep(4)}
          />
        )}
        {step === 6 && job && (
          <Step6Confirm
            job={job}
            isSales={isSales}
            importType={importType!}
            conflict={conflict}
            setConflict={setConflict}
            useSandbox={useSandbox}
            setUseSandbox={setUseSandbox}
            executing={execute.isPending}
            onExecute={() => execute.mutate()}
            onBack={() => setStep(5)}
          />
        )}
        {step === 7 && job && (
          <Step7Progress
            job={job}
            setJob={setJob}
            isSales={isSales}
            onRestart={restart}
            onViewList={() => setStep(0)}
          />
        )}

        {step === 0 && (
          <HistoryList items={history ?? []} onPickType={() => setStep(1)} />
        )}
      </div>
    </>
  );
}

/* ─── Step Indicator ─────────────────────────────── */
function StepIndicator({ step }: { step: number }) {
  const labels = ['选类型', '选来源', '上传文件', '字段映射', 'dry-run', '确认导入', '进度结果'];
  return (
    <div className="import-stepper">
      {labels.map((l, i) => {
        const idx = i + 1;
        const cls = idx === step ? 'current' : idx < step ? 'done' : 'pending';
        return (
          <div key={l} className={`import-stepper-item ${cls}`}>
            <div className="import-stepper-dot">{idx}</div>
            <div className="import-stepper-label">{l}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Step 1: 选类型 ─────────────────────────────── */
function Step1Type({ onSelect }: { onSelect: (t: ImportType) => void }) {
  return (
    <div className="import-card">
      <div className="import-card-title">第 1 步 · 选择导入类型</div>
      <div className="import-type-grid">
        {IMPORT_TYPES.map((t) => (
          <div key={t.key} className="import-type-card" onClick={() => onSelect(t.key)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontWeight: 500 }}>{t.label}</span>
              {t.tag && <Pill tone="primary">{t.tag}</Pill>}
            </div>
            <div className="time-text" style={{ color: 'var(--text-3)' }}>{t.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Step 2: 选来源 ─────────────────────────────── */
function Step2Source({ onSelect, onBack, importType }: { onSelect: (s: SourceType) => void; onBack: () => void; importType: ImportType }) {
  const typeLabel = IMPORT_TYPES.find((t) => t.key === importType)?.label;
  return (
    <div className="import-card">
      <div className="import-card-title">第 2 步 · 选择来源（导入类型：{typeLabel}）</div>
      <div className="import-source-grid">
        {SOURCE_TYPES.map((s) => (
          <div key={s.key} className="import-source-card" onClick={() => onSelect(s.key)}>
            <div style={{ fontWeight: 500, marginBottom: 6 }}>{s.label}</div>
            <div className="time-text" style={{ color: 'var(--text-3)' }}>{s.desc}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
        {/* 通用模板下载 */}
        <Btn kind="text" onClick={() => message.info('模板下载在 C-2 之后续 commit 接通')}>下载 Excel 模板</Btn>
      </div>
    </div>
  );
}

/* ─── Step 3: 上传文件 ─────────────────────────────── */
function Step3Upload({ onUpload, uploading, onBack }: { onUpload: (f: File) => void; uploading: boolean; onBack: () => void }) {
  const props: UploadProps = {
    accept: '.xlsx,.xls,.csv,.zip',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      const sizeMB = file.size / 1024 / 1024;
      if (sizeMB > 200) {
        message.error('文件超过 200MB，请压缩或拆分');
        return Upload.LIST_IGNORE;
      }
      onUpload(file);
      return false; // 不让 antd 自动上传，自己控制
    },
  };
  return (
    <div className="import-card">
      <div className="import-card-title">第 3 步 · 上传文件</div>
      <Upload.Dragger {...props} disabled={uploading} style={{ marginTop: 12 }}>
        <p style={{ fontSize: 36, color: 'var(--primary)' }}>⤴</p>
        <p style={{ fontSize: 14, fontWeight: 500 }}>
          {uploading ? '上传中…' : '拖拽文件到此处，或点击选择'}
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-3)' }}>
          支持 .xlsx / .xls / .csv / .zip（zip 内包裹 xlsx 自动解压）· 单文件 ≤ 200MB
        </p>
      </Upload.Dragger>
      <div style={{ marginTop: 16 }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
      </div>
    </div>
  );
}

/* ─── Step 4: 字段映射 ─────────────────────────────── */
function Step4Mapping({
  columns, schema, mapping, setMapping, onNext, onBack, saving, importType, sourceType,
}: {
  columns: string[];
  schema: ImportSchema;
  mapping: Record<string, string>;
  setMapping: (m: Record<string, string>) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  importType: ImportType;
  sourceType: SourceType;
}) {
  const targetOptions = useMemo(() => {
    const targets = Array.from(new Set(Object.values(schema.sources).map((s) => s.target)));
    return [{ value: '', label: '— 不导入此列 —' }, ...targets.map((t) => ({ value: t, label: t }))];
  }, [schema]);

  // 计算必填字段是否都已映射
  const requiredTargets = useMemo(() =>
    Object.values(schema.sources).filter((s) => s.required).map((s) => s.target),
  [schema]);
  const mappedTargets = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = requiredTargets.filter((t) => !mappedTargets.has(t));

  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState('');
  const saveTpl = useMutation({
    mutationFn: () => $.post('/imports/templates', {
      name: tplName, importType, sourceType, fieldMapping: mapping,
    }),
    onSuccess: () => {
      toast.success('模板已保存，下次同格式可直接套用');
      setSaveTplOpen(false);
      setTplName('');
    },
  });

  return (
    <div className="import-card">
      <div className="import-card-title">
        第 4 步 · 字段映射
        <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-3)', fontWeight: 400 }}>
          {schema.label} · {columns.length} 个源列 · 已映射 {Object.values(mapping).filter(Boolean).length} 个
        </span>
      </div>

      {missingRequired.length > 0 && (
        <div style={{ background: 'var(--danger-bg)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12, color: 'var(--danger-text)' }}>
          ⚠ 必填字段未映射：{missingRequired.join(' / ')}
        </div>
      )}

      <table className="ck-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '35%' }}>源文件列名</th>
            <th style={{ width: '5%' }}></th>
            <th style={{ width: '40%' }}>映射到（系统字段）</th>
            <th style={{ width: '20%' }}>类型 / 提示</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col) => {
            const target = mapping[col] ?? '';
            const fieldDef = Object.values(schema.sources).find((s) => s.target === target);
            return (
              <tr key={col}>
                <td><span className="time-text" style={{ fontFamily: 'monospace' }}>{col}</span></td>
                <td style={{ color: 'var(--text-3)' }}>→</td>
                <td>
                  <Select
                    value={target}
                    onChange={(v) => setMapping({ ...mapping, [col]: v })}
                    options={targetOptions}
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="不导入"
                  />
                </td>
                <td>
                  {fieldDef && (
                    <div>
                      <Pill tone={fieldDef.required ? 'danger' : 'default'}>{fieldDef.type}{fieldDef.required ? ' · 必填' : ''}</Pill>
                      {fieldDef.note && <div className="time-sub" style={{ marginTop: 4 }}>{fieldDef.note}</div>}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn kind="text" onClick={() => setSaveTplOpen(true)}>保存为模板</Btn>
          <Btn kind="primary" onClick={onNext} disabled={missingRequired.length > 0 || saving}>
            {saving ? '保存中…' : '下一步：dry-run 预检 →'}
          </Btn>
        </div>
      </div>

      <Modal
        open={saveTplOpen}
        title="保存字段映射为模板"
        onCancel={() => setSaveTplOpen(false)}
        onOk={() => saveTpl.mutate()}
        okButtonProps={{ disabled: !tplName, loading: saveTpl.isPending }}
      >
        <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="模板名（如：钉钉客户表 - 2026 通用版）" />
      </Modal>
    </div>
  );
}

/* ─── Step 5: dry-run ─────────────────────────────── */
function Step5DryRun({ job, running, onRun, onNext, onBack }: { job: JobState; running: boolean; onRun: () => void; onNext: () => void; onBack: () => void }) {
  const ran = job.status === 'previewed' || job.successCount > 0 || job.failCount > 0 || job.skipCount > 0;
  const dr = job.dryRunResult;
  const failRows = (dr?.rows ?? []).filter((r) => r.action === 'fail');
  const warnRows = (dr?.rows ?? []).filter((r) => r.action === 'warn');
  const [showAllFails, setShowAllFails] = useState(false);
  const failDisplay = showAllFails ? failRows : failRows.slice(0, 10);

  return (
    <div className="import-card">
      <div className="import-card-title">第 5 步 · dry-run 预检</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
        预检会遍历每行按字段类型校验，并检测客户重复、跨人持有冲突等。<strong>不会真正写入数据库</strong>，可以多次重跑。
      </div>

      {!ran ? (
        <Btn kind="primary" onClick={onRun} disabled={running}>
          {running ? '校验中…' : '开始 dry-run'}
        </Btn>
      ) : (
        <>
          <StatsRow>
            <StatCard label="总行数" value={job.totalRows} unit="行" />
            <StatCard label="将导入" value={job.successCount} unit="行" valueColor="success" />
            <StatCard label="将跳过" value={job.skipCount} unit="行" />
            <StatCard label="将失败" value={job.failCount} unit="行" valueColor="danger" />
          </StatsRow>

          {job.errorReportUrl && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--warning-bg)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13 }}>📋 后端已生成失败行 Excel，修正后可重新上传</span>
              <a
                href={job.errorReportUrl}
                target="_blank"
                rel="noreferrer"
                download
                onClick={() => toast.success('已开始下载 失败行.xlsx')}
              >
                <Btn kind="primary">下载失败行 Excel</Btn>
              </a>
            </div>
          )}

          {warnRows.length > 0 && (
            <div style={{ marginTop: 12, padding: 12, background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>
                ⚠️ {warnRows.length} 行需要人工确认（仍会导入但标记为 manual_review）
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: 'var(--text-2)' }}>
                {warnRows.slice(0, 5).map((r) => (
                  <li key={r.row}>第 {r.row} 行：{r.reason}</li>
                ))}
                {warnRows.length > 5 && <li style={{ color: 'var(--text-3)' }}>…共 {warnRows.length} 条</li>}
              </ul>
            </div>
          )}

          {failRows.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>
                ❌ 失败行清单（{failRows.length} 条）
              </div>
              <table className="ck-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>行号</th>
                    <th>失败原因</th>
                  </tr>
                </thead>
                <tbody>
                  {failDisplay.map((r) => (
                    <tr key={r.row}>
                      <td>{r.row}</td>
                      <td>{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {failRows.length > 10 && (
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <Btn kind="text" onClick={() => setShowAllFails((v) => !v)}>
                    {showAllFails ? `折叠（只看前 10 条）` : `查看全部 ${failRows.length} 条 ↓`}
                  </Btn>
                </div>
              )}

              {dr && dr.errorSummary.length > 0 && (
                <div style={{ marginTop: 12, padding: 10, background: 'var(--bg)', borderRadius: 4, fontSize: 12 }}>
                  <div style={{ color: 'var(--text-3)', marginBottom: 4 }}>错误聚合（top 5）：</div>
                  {dr.errorSummary.map((e, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                      <span>{e.reason}</span>
                      <span style={{ color: 'var(--text-3)' }}>× {e.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {failRows.length === 0 && warnRows.length === 0 && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--success-bg)', borderRadius: 6, fontSize: 13, color: 'var(--success-text)' }}>
              ✅ 全部 {job.successCount} 行校验通过，无失败、无警告
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
        {ran && (
          <Btn kind="primary" onClick={onNext}>下一步：确认导入 →</Btn>
        )}
      </div>
    </div>
  );
}

/* ─── Step 6: 确认导入 ─────────────────────────────── */
function Step6Confirm({
  job, isSales, importType, conflict, setConflict, useSandbox, setUseSandbox, executing, onExecute, onBack,
}: {
  job: JobState; isSales: boolean;
  importType: ImportType;
  conflict: 'skip' | 'overwrite' | 'transfer';
  setConflict: (s: 'skip' | 'overwrite' | 'transfer') => void;
  useSandbox: boolean;
  setUseSandbox: (v: boolean) => void;
  executing: boolean; onExecute: () => void; onBack: () => void;
}) {
  // 沙盒默认勾选规则：客户主表 > 50 行
  // 用 effect 仅初始化一次（不让用户后续主动取消又被覆盖）
  const initRef = useMemo(() => ({ done: false }), []);
  useEffect(() => {
    if (initRef.done) return;
    initRef.done = true;
    const recommend = importType === 'customers' && job.totalRows > 50;
    if (recommend && !useSandbox) setUseSandbox(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // C-4.5 P1：沙盒勾选时强制 conflict=skip（overwrite/transfer 在沙盒里没意义）
  // 用 effect 监听 useSandbox 变化 — 用户勾选沙盒立即把 conflict 重置 skip
  useEffect(() => {
    if (useSandbox && conflict !== 'skip') setConflict('skip');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useSandbox]);

  return (
    <div className="import-card">
      <div className="import-card-title">第 6 步 · 确认导入</div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, marginBottom: 8 }}>客户重复时如何处理：</div>
        <div className="import-conflict-grid">
          {CONFLICT_STRATEGY.map((s) => {
            // 销售员禁 overwrite/transfer；沙盒模式 也禁
            const sandboxDisabled = useSandbox && s.key !== 'skip';
            const salesDisabled = isSales && s.key !== 'skip';
            const disabled = sandboxDisabled || salesDisabled;
            const lockTip = sandboxDisabled ? '(沙盒模式不可用)' : salesDisabled ? '(需主管权限)' : '';
            return (
              <div
                key={s.key}
                className={`import-conflict-card ${conflict === s.key ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setConflict(s.key as any)}
              >
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {s.label} {lockTip && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{lockTip}</span>}
                </div>
                <div className="time-text" style={{ color: 'var(--text-3)' }}>{s.desc}</div>
              </div>
            );
          })}
        </div>
        {useSandbox && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>
            ℹ️ 沙盒模式下仅支持 <strong>跳过</strong> 策略，避免污染生产数据。如需 overwrite/transfer，请关闭沙盒模式。
          </div>
        )}
      </div>

      {/* C-4.5: 沙盒模式 toggle */}
      <div
        onClick={() => setUseSandbox(!useSandbox)}
        style={{
          marginBottom: 16, padding: 12, borderRadius: 6,
          border: `1px solid ${useSandbox ? 'var(--primary)' : 'var(--border)'}`,
          background: useSandbox ? 'var(--primary-bg, #e6f7ff)' : 'var(--bg)',
          cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start',
        }}
      >
        <input
          type="checkbox"
          checked={useSandbox}
          onChange={(e) => setUseSandbox(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: 3, flex: '0 0 16px', width: 16, height: 16 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>
            🧪 沙盒模式（推荐首次导入 &gt; 50 行客户时使用）
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>
            勾选后导入到沙盒空间，<strong>不污染正式数据</strong>。完成后到 <code>/admin/data-import/sandbox/{job.id}</code> 验证：
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              <li>数据无误 → 一键正式导入</li>
              <li>有错 → 一键全部撤销，不留痕迹</li>
            </ul>
          </div>
        </div>
      </div>

      {isSales && (
        <div style={{ background: 'var(--warning-bg)', padding: 12, borderRadius: 6, fontSize: 12, marginBottom: 16 }}>
          💡 销售员导入需要主管审批。提交后任务进入 <Pill tone="warning">approving</Pill> 状态，主管在 /approvals 通过后才会真正执行。
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
        <Btn kind="primary" onClick={onExecute} disabled={executing}>
          {executing ? '提交中…' : useSandbox ? '写入沙盒' : isSales ? '提交审批' : '开始导入'}
        </Btn>
      </div>
    </div>
  );
}

/* ─── Step 7: 进度 + 结果 ─────────────────────────────── */
function Step7Progress({ job, setJob, isSales, onRestart, onViewList }: {
  job: JobState;
  setJob: (j: JobState) => void;
  isSales: boolean;
  onRestart: () => void;
  onViewList: () => void;
}) {
  // 终态：不再轮询
  const isFinal = ['done', 'partial', 'failed', 'cancelled', 'rolled_back'].includes(job.status);
  // approving：等主管，前端用 5s 间隔慢轮询（让 sales 看到审批完成）
  // running：1.5s 间隔快轮询，实时刷进度
  const isApproving = job.status === 'approving';
  const isRunning = job.status === 'running';

  // 轮询 id 用 ref，便于 cleanup 时清掉
  const timerRef = useRef<number | null>(null);
  const finishedToastedRef = useRef(false);

  useEffect(() => {
    if (isFinal) {
      if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
      // 一次性 toast 完成提示
      if (!finishedToastedRef.current) {
        finishedToastedRef.current = true;
        if (job.status === 'done') toast.success(`导入完成：${job.successCount} 行成功`);
        else if (job.status === 'partial') toast.success(`部分完成：成功 ${job.successCount}，失败 ${job.failCount}`);
        else if (job.status === 'failed') toast.error(`导入失败${job.notes ? '：' + job.notes : ''}`);
        else if (job.status === 'cancelled') toast.error(`已取消${job.notes ? '：' + job.notes : ''}`);
      }
      return;
    }
    if (!isApproving && !isRunning) return;

    const interval = isRunning ? 1500 : 5000;
    const tick = async () => {
      // visibility API：标签页不在前台时跳过 fetch
      if (document.visibilityState !== 'visible') return;
      try {
        const fresh = await $.get<JobState>(`/imports/${job.id}`);
        setJob(fresh);
      } catch {
        // 静默错误（401 会被全局拦截；其他网络错只是错过一次 tick）
      }
    };
    // 立刻拉一次 + 起 interval
    tick();
    timerRef.current = window.setInterval(tick, interval);
    return () => {
      if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    };
    // 依赖 status 变化时重启 timer（approving → running 间隔会变 5s→1.5s）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, job.status]);

  const pct = job.progressPercentage ?? 0;
  const phase = job.currentPhase ?? '';

  return (
    <div className="import-card">
      <div className="import-card-title">第 7 步 · {isFinal ? '结果' : '进度'}</div>

      {isApproving && (
        <div style={{ padding: 20, background: 'var(--warning-bg)', borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>⏳ 已提交审批</div>
          <div style={{ color: 'var(--text-2)', fontSize: 13 }}>
            等主管在 <code>/approvals</code> 通过后会自动执行导入（每 5 秒刷新一次状态）
          </div>
        </div>
      )}

      {isRunning && (
        <>
          <Progress percent={pct} status="active" strokeColor="var(--primary)" />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>
            <span>{phase || '正在写入数据库…'}</span>
            <span>{job.successCount + job.skipCount + job.failCount} / {job.totalRows} 行</span>
          </div>
        </>
      )}

      {isFinal && (
        <>
          <StatsRow>
            <StatCard label="总行数" value={job.totalRows} unit="行" />
            <StatCard label="成功" value={job.successCount} unit="行" valueColor="success" />
            <StatCard label="跳过" value={job.skipCount} unit="行" />
            <StatCard label="失败" value={job.failCount} unit="行" valueColor="danger" />
          </StatsRow>
          {/* C-4.5 沙盒：完成后引导到沙盒预览页 */}
          {!!job.isSandbox && job.status === 'done' && (
            <div style={{ marginTop: 16, padding: 14, background: 'var(--primary-bg, #e6f7ff)', borderRadius: 8, border: '1px solid var(--primary)' }}>
              <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 6 }}>
                🧪 已写入沙盒（{job.successCount} 行）
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
                正式客户列表 <strong>看不到</strong>这些数据。验证后再正式导入：
              </div>
              <Link to={`/admin/data-import/sandbox/${job.id}`}>
                <Btn kind="primary">→ 进入沙盒预览页</Btn>
              </Link>
            </div>
          )}
          {job.notes && (job.status === 'failed' || job.status === 'cancelled') && (
            <div style={{ marginTop: 12, padding: 10, background: 'var(--danger-bg)', color: 'var(--danger-text)', borderRadius: 6, fontSize: 12 }}>
              {job.notes}
            </div>
          )}
          {job.errorReportUrl && (
            <div style={{ marginTop: 12 }}>
              <a href={job.errorReportUrl} target="_blank" rel="noreferrer" download>
                <Btn kind="default">下载失败行 Excel</Btn>
              </a>
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
        <Btn kind="primary" onClick={onRestart}>开始下一次导入</Btn>
        <Btn kind="text" onClick={onViewList}>查看导入历史</Btn>
      </div>
    </div>
  );
}

/* ─── 历史列表（含 C-5 撤销按钮）─────────────── */
function HistoryList({ items, onPickType }: { items: JobState[]; onPickType: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [rollbackTarget, setRollbackTarget] = useState<JobState | null>(null);
  const [rollbackPreview, setRollbackPreview] = useState<any>(null);
  const [checking, setChecking] = useState(false);
  // owner 变更确认 — 产品决策要求用户必须勾才能继续撤销
  const [ackOwner, setAckOwner] = useState(false);

  const startRollback = async (job: JobState) => {
    setRollbackTarget(job);
    setRollbackPreview(null);
    setAckOwner(false);
    setChecking(true);
    try {
      const data = await $.post(`/imports/${job.id}/rollback-check`);
      setRollbackPreview(data);
    } catch (e: any) {
      setRollbackTarget(null);
    } finally {
      setChecking(false);
    }
  };

  const rollback = useMutation({
    mutationFn: () => $.post(`/imports/${rollbackTarget!.id}/rollback`),
    onSuccess: (r: any) => {
      const data = r ?? {};
      toast.success(`已撤销 ${data.rolledBack ?? '?'} 行，保留 ${data.kept ?? 0} 行`);
      setRollbackTarget(null);
      setRollbackPreview(null);
      qc.invalidateQueries({ queryKey: ['imports-list'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      // 跳客户列表带 banner
      nav(`/customers?fromRollback=${rollbackTarget!.id}&rolledBack=${data.rolledBack ?? 0}`, { replace: true });
    },
  });

  if (items.length === 0) {
    return (
      <div className="import-card">
        <Empty description="还没有任何导入记录" />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Btn kind="primary" onClick={onPickType}>开始首次导入</Btn>
        </div>
      </div>
    );
  }
  return (
    <div className="import-card">
      <div className="import-card-title">导入历史</div>
      <table className="ck-table">
        <thead>
          <tr>
            <th>ID</th><th>类型</th><th>文件名</th><th>状态</th><th>结果</th><th>提交时间</th><th>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((j) => {
            const ageDays = j.createdAt ? (Date.now() - new Date(j.createdAt).getTime()) / 86_400_000 : 0;
            const canRollback = (j.status === 'done' || j.status === 'partial')
              && !j.isSandbox
              && ageDays <= 30
              && (j.insertedRowIds?.length ?? 0) > 0;
            const overWindow = ageDays > 30;
            return (
              <tr key={j.id}>
                <td><span className="time-text">#{j.id}</span></td>
                <td><Pill tone="default">{IMPORT_TYPES.find((t) => t.key === j.importType)?.label ?? j.importType}</Pill></td>
                <td><span className="time-text" style={{ fontFamily: 'monospace' }}>{j.fileName ?? '-'}</span></td>
                <td>
                  {!!j.isSandbox && <Tag color="blue" style={{ marginRight: 4 }}>沙盒</Tag>}
                  <Pill tone={statusTone(j.status)}>{statusLabel(j.status)}</Pill>
                </td>
                <td><span className="time-text">{j.successCount} / {j.failCount} / {j.skipCount}</span></td>
                <td><span className="time-text">{fmtDateTimeSec(j.createdAt)}</span></td>
                <td>
                  {!!j.isSandbox && j.status === 'done' && (
                    <Link to={`/admin/data-import/sandbox/${j.id}`}>
                      <Btn kind="text">查看沙盒</Btn>
                    </Link>
                  )}
                  {canRollback && (
                    <Btn kind="danger" onClick={() => startRollback(j)}>撤销</Btn>
                  )}
                  {!canRollback && j.status === 'rolled_back' && (
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                      已撤销{(j as any).rolledBackAt ? ` · ${fmtRelative((j as any).rolledBackAt)}` : ''}
                    </span>
                  )}
                  {!canRollback && overWindow && (j.status === 'done' || j.status === 'partial') && !j.isSandbox && (
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }} title="超过 30 天">归档</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* 撤销预检 Modal */}
      <Modal
        open={rollbackTarget !== null}
        title={`撤销导入 #${rollbackTarget?.id} — ${IMPORT_TYPES.find((t) => t.key === rollbackTarget?.importType)?.label ?? rollbackTarget?.importType}`}
        onCancel={() => { setRollbackTarget(null); setRollbackPreview(null); setAckOwner(false); }}
        onOk={() => rollback.mutate()}
        okButtonProps={{
          danger: true,
          loading: rollback.isPending,
          // 禁用条件：无可撤销行 OR (有 ownerChangedRows 但用户没勾确认)
          disabled: !rollbackPreview
            || rollbackPreview.rollbackable.length === 0
            || ((rollbackPreview.ownerChangedRows?.length ?? 0) > 0 && !ackOwner),
        }}
        okText={rollbackPreview ? `确认撤销 ${rollbackPreview.rollbackable.length} 行` : '撤销'}
        cancelText="取消"
        width={620}
      >
        {checking && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)' }}>预检中…</div>}
        {rollbackPreview && (
          <RollbackPreview preview={rollbackPreview} ackOwner={ackOwner} setAckOwner={setAckOwner} />
        )}
      </Modal>
    </div>
  );
}

function RollbackPreview({ preview, ackOwner, setAckOwner }: {
  preview: any;
  ackOwner: boolean;
  setAckOwner: (v: boolean) => void;
}) {
  const { total, rollbackable, blocked, ageDays, importType, ownerChangedRows = [] } = preview;
  const ageStr = `${Math.floor(ageDays)} 天前`;
  return (
    <>
      <div style={{ marginBottom: 16, fontSize: 13 }}>
        共 <strong>{total}</strong> 行（{importType} · 提交于 {ageStr}），
        可撤销 <strong style={{ color: 'var(--success)' }}>{rollbackable.length}</strong> 行，
        保留 <strong style={{ color: 'var(--warning)' }}>{blocked.length}</strong> 行
      </div>

      {/* owner 变更警示卡片（产品决策：放行 + UI 警示） */}
      {ownerChangedRows.length > 0 && (
        <div style={{ marginBottom: 12, padding: 12, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
          <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 6 }}>
            ⚠️ 撤销将影响其他销售员
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
            以下 <strong>{ownerChangedRows.length}</strong> 个客户已被转给其他销售员，撤销后他们的客户列表会立刻减少（30 天内可恢复）：
          </div>
          <ul style={{ margin: '4px 0 8px 16px', padding: 0, fontSize: 12 }}>
            {ownerChangedRows.slice(0, 5).map((r: any) => (
              <li key={r.customerId}>
                <strong>{r.nickname}</strong>（原 {r.originalOwnerName} → 现 <span style={{ color: 'var(--warning)' }}>{r.currentOwnerName}</span>）
              </li>
            ))}
            {ownerChangedRows.length > 5 && (
              <li style={{ color: 'var(--text-3)' }}>…还有 {ownerChangedRows.length - 5} 个</li>
            )}
          </ul>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
            <input
              type="checkbox"
              checked={ackOwner}
              onChange={(e) => setAckOwner(e.target.checked)}
            />
            <span>我知道这会影响其他销售员，仍要撤销</span>
          </label>
        </div>
      )}

      {blocked.length === 0 && rollbackable.length > 0 && ownerChangedRows.length === 0 && (
        <div style={{ padding: 10, background: 'var(--success-bg, #f6ffed)', borderRadius: 6, fontSize: 12 }}>
          ✅ 全部 {rollbackable.length} 行可以安全撤销（无关联业务记录）
        </div>
      )}
      {rollbackable.length === 0 && (
        <div style={{ padding: 10, background: 'var(--danger-bg)', color: 'var(--danger-text)', borderRadius: 6, fontSize: 12 }}>
          ❌ 全部 {total} 行都已被后续操作，无法撤销
        </div>
      )}

      {blocked.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>
            被保留的行（点撤销不会动它们）：
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4 }}>
            <table className="ck-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>客户 ID</th>
                  <th>原因</th>
                </tr>
              </thead>
              <tbody>
                {blocked.slice(0, 50).map((b: any) => (
                  <tr key={b.id}>
                    <td>#{b.id}</td>
                    <td>{b.reasons.join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {blocked.length > 50 && (
              <div style={{ padding: 6, textAlign: 'center', fontSize: 11, color: 'var(--text-3)' }}>
                …还有 {blocked.length - 50} 行
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, padding: 10, background: 'var(--bg)', borderRadius: 4, fontSize: 11, color: 'var(--text-3)' }}>
        💡 撤销 = 软删除（deleted_at 设为当前时间）。客户列表不再显示，但数据保留 30 天可恢复。
      </div>
    </>
  );
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    pending: '草稿', dry_run: 'dry-run', previewed: '已预检', approving: '审批中',
    running: '导入中', done: '已完成', failed: '失败', partial: '部分成功',
    cancelled: '已取消', rolled_back: '已撤销',
  };
  return map[s] ?? s;
}
function statusTone(s: string): any {
  if (s === 'done') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'approving' || s === 'running') return 'warning';
  if (s === 'cancelled' || s === 'rolled_back') return 'default';
  return 'primary';
}
